<template>
  <RouterLink :to="link">
    <Card variant="secondary" size="medium">
      <View gap="md" direction="column">
        <View gap="md" direction="row">
          <PropertyPhotoPreview :image-url="item.coverImage" size="md" />

          <View gap="xs" direction="column" justify="space-between">
            <Text variant="h5">{{ item.displayName }}</Text>

            <div>
              <View gap="sm">
                <div>
                  <Text variant="h3" as="span">{{ item.metrics.leadCount }}</Text>
                  <Text variant="label-small" color="tertiary" as="span">&nbsp;Leads</Text>
                </div>
                <View
                  v-if="percentageChange"
                  gap="xs"
                  padding="xs"
                  align="center"
                  :class="['badge', isPercentageChangePositive ? 'positive' : 'negative']"
                >
                  <Icon name="down-pointing-triangle" size="extra-small" class="badge-icon" />
                  <span>{{ percentageChange }}%</span>
                </View>
              </View>
            </div>

            <Text variant="body-small" color="secondary">Posted {{ daysOnMarket }} ago</Text>
          </View>
        </View>
      </View>
    </Card>
  </RouterLink>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { Badge, Card, Icon, Text, View } from '@tallo/design-system';
import { propertyRoute } from '@tallo/investor/navigation';
import { PropertyPhotoPreview } from '@tallo/property';

import { UnitPerformanceScoreDto } from '../interfaces/property-performance.dto';

const props = defineProps<{ item: UnitPerformanceScoreDto }>();
const link = computed(() => propertyRoute(props.item.propertyId));
const percentageChange = computed(() => props.item.periodComparison?.percentageChange || 0);
const isPercentageChangePositive = computed(() => percentageChange.value > 0);
const daysOnMarket = computed(() => {
  const days = Math.floor(props.item.daysOnMarket);
  return days > 1 ? `${days} days` : `${days} day`;
});
</script>

<style scoped lang="scss">
.badge {
  border-radius: var(--t-border-radius-xs);
  font-weight: bold;
  font-size: 0.625rem;
  line-height: 0.625rem;
  align-self: center;
  gap: 0.18rem;

  &.positive {
    background-color: var(--t-color-green-200);
    color: var(--t-color-green-600);

    .badge-icon {
      transform: rotate(180deg);
    }
  }

  &.negative {
    background-color: var(--t-color-red-200);
    color: var(--t-color-red-600);
  }

  .badge-icon {
    --t-icon-size: 0.5rem;
  }
}
</style>
